import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Truck, Phone, Radio, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ShipmentStatusDialog } from "@/components/ShipmentStatusDialog";

export default function TrackingPage() {
  const [drivers, setDrivers] = useState([]);
  const [focus, setFocus] = useState(null); // order_number to isolate, null = show all
  const [statusFor, setStatusFor] = useState(null); // driver row for status dialog
  const mapEl = useRef(null);
  const mapObj = useRef(null);
  const markers = useRef([]);

  const load = () => api.get("/tracking/active").then((r) => setDrivers(r.data.drivers || [])).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  useEffect(() => {
    if (!mapEl.current || mapObj.current) return;
    mapObj.current = L.map(mapEl.current, { scrollWheelZoom: false }).setView([22.9734, 78.6569], 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors", maxZoom: 19,
    }).addTo(mapObj.current);
  }, []);

  useEffect(() => {
    const map = mapObj.current;
    if (!map) return;
    markers.current.forEach((m) => map.removeLayer(m));
    markers.current = [];
    const pts = [];
    const shown = focus ? drivers.filter((d) => d.order_number === focus) : drivers;
    shown.forEach((d) => {
      if (d.lat == null || d.lng == null) return;
      const color = d.active ? "#10b981" : "#f59e0b";
      const m = L.circleMarker([d.lat, d.lng], { radius: 11, color, fillColor: color, fillOpacity: 0.9, weight: 2 }).addTo(map);
      const label = `#${d.sr_no} · ${d.order_number}`;
      m.bindTooltip(label, { permanent: true, direction: "top", offset: [0, -10], className: "driver-label" });
      m.bindPopup(`<b>#${d.sr_no} ${d.order_number}</b><br/>LR: ${d.lr_number || "—"}<br/>Driver: ${d.driver_name}<br/>Vehicle: ${d.vehicle_type}<br/>${d.route}`);
      markers.current.push(m);
      pts.push([d.lat, d.lng]);
    });
    if (focus && pts.length === 1) { map.setView(pts[0], 13); }
    else if (pts.length) { try { map.fitBounds(pts, { padding: [50, 50], maxZoom: 12 }); } catch { /* single point */ } }
  }, [drivers, focus]);

  const withGps = drivers.filter((d) => d.has_location);

  return (
    <div data-testid="tracking-page">
      <style>{`.driver-label{background:#0f172a;color:#fff;border:none;box-shadow:0 1px 4px rgba(0,0,0,.4);font-weight:700;font-size:11px;padding:2px 7px;border-radius:2px}.driver-label::before{display:none}.leaflet-container{background:#1a1a1a;font-family:inherit}`}</style>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-primary">Operations</div>
          <h1 className="font-heading font-black text-2xl sm:text-4xl tracking-tight">Live Driver Tracking</h1>
          <p className="text-muted-foreground text-sm">{drivers.length} order{drivers.length === 1 ? "" : "s"} on the road · {withGps.filter((d) => d.active).length} sharing GPS · refreshes every 30s.</p>
        </div>
        {focus && (
          <Button variant="outline" className="rounded-none gap-2" onClick={() => setFocus(null)} data-testid="show-all-btn"><Layers className="h-4 w-4" /> Show All</Button>
        )}
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-4">
        {/* Driver list */}
        <div className="border border-border bg-card order-2 lg:order-1" data-testid="driver-list">
          <div className="px-4 py-3 border-b border-border text-xs font-bold uppercase tracking-widest text-muted-foreground">Drivers</div>
          <div className="divide-y divide-border max-h-[520px] overflow-y-auto">
            {drivers.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No active shipments. Assign a driver mobile and send a tracking link on an order.</div>}
            {drivers.map((d) => (
              <button
                key={d.order_number}
                onClick={() => setFocus(focus === d.order_number ? null : d.order_number)}
                className={`w-full text-left p-3 transition-colors hover:bg-muted ${focus === d.order_number ? "bg-muted ring-1 ring-primary" : ""}`}
                data-testid={`driver-item-${d.order_number}`}
              >
                <div className="flex items-center gap-2">
                  <span className="h-6 w-6 shrink-0 grid place-items-center bg-primary text-primary-foreground text-[11px] font-bold font-mono-num">{d.sr_no}</span>
                  <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${d.active ? "bg-emerald-500" : "bg-amber-500"}`} title={d.active ? "GPS Active" : "GPS Inactive"} />
                  <span className="font-mono-num font-bold text-sm truncate">{d.order_number}</span>
                  <span className={`ml-auto text-[10px] font-bold uppercase px-1.5 py-0.5 ${d.active ? "text-emerald-500" : "text-amber-500"}`}>{d.active ? "Active" : "Inactive"}</span>
                </div>
                <div className="mt-1.5 pl-8 space-y-0.5 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1"><Truck className="h-3 w-3" /> {d.driver_name} · {d.vehicle_type}</div>
                  <div className="flex items-center gap-1"><Phone className="h-3 w-3" /> {d.driver_mobile}</div>
                  {d.lr_number && <div className="flex items-center gap-1"><MapPin className="h-3 w-3" /> LR {d.lr_number}</div>}
                  <div className="flex items-center gap-1 text-foreground/70">{d.route}</div>
                  <div className="flex items-center gap-1 pt-0.5"><Radio className="h-3 w-3 text-primary" /> {d.shipment_label}{d.eta ? ` · ETA ${d.eta}` : ""}</div>
                </div>
                <div className="mt-2 pl-8">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setStatusFor(d); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setStatusFor(d); } }}
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-orange-500 text-white px-2 py-1 hover:bg-orange-600 transition-colors cursor-pointer"
                    data-testid={`update-status-${d.order_number}`}
                  ><Truck className="h-3 w-3" /> Update Status</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Map */}
        <div className="border border-border bg-card order-1 lg:order-2">
          {focus && <div className="px-4 py-2 border-b border-border text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-2" data-testid="isolation-banner"><Truck className="h-3.5 w-3.5" /> Isolated: {focus}</div>}
          <div ref={mapEl} style={{ height: 520, width: "100%" }} />
        </div>
      </div>

      <ShipmentStatusDialog lead={statusFor} onClose={() => setStatusFor(null)} onSaved={() => { setStatusFor(null); load(); }} />
    </div>
  );
}
